import { IHeaderData } from '../AppTable'
import React, { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTouchMove } from './useTouchMove'
import { useTranslation } from 'react-i18next'
import { useGetWidth } from 'utils/useGetWidth'

const moveItem = (
  headerData: IHeaderData[],
  fromIndex: number,
  toIndex: number,
) => {
  const newArray = [...headerData]
  const [movedItem] = newArray.splice(fromIndex, 1)
  newArray.splice(toIndex, 0, movedItem)
  return newArray
}

interface THeadMobileBlock {
  mobileMode?: boolean
  currentCol: number
  headerData: IHeaderData[]
  setCurrentCol: React.Dispatch<React.SetStateAction<number>>
  setCurrentSortFn?: (value: string) => void
  onToggleAll?: () => void
  isAllOpen?: boolean
  chooseMode?: boolean
  setHeaderData: (value: IHeaderData[]) => void
  tableName: string
}

export const THeadMobileDndBlock = React.memo(
  ({
    headerData,
    mobileMode,
    currentCol,
    setCurrentCol,
    setCurrentSortFn,
    onToggleAll,
    isAllOpen,
    chooseMode,
    setHeaderData,
    tableName,
  }: THeadMobileBlock) => {
    const { t } = useTranslation()
    const [searchParams, setSearchParams] = useSearchParams()
    const { width } = useGetWidth()
    const [currentDataTitle, setCurrentDataTitle] =
      useState<IHeaderData | null>(null)
    const ordering = searchParams.get('ordering')
    const sortStartValue = ordering
      ? { name: ordering.replace('-', ''), activeField: ordering }
      : null
    const [activeSort, setActiveSort] = useState<{
      name: string
      activeField: string
    } | null>(sortStartValue)
    const touchFn = useTouchMove({
      totalCount: headerData.length,
      currentCol,
      setCurrentCol,
    })

    const onSortHandler = useCallback(
      (name: string) => {
        const sortValue =
          activeSort?.name === name
            ? activeSort?.activeField === name
              ? `-${name}`
              : name
            : name
        setActiveSort({
          name,
          activeField: sortValue,
        })
        if (setCurrentSortFn) {
          setCurrentSortFn(sortValue)
        }
        if (!setCurrentSortFn) {
          searchParams.set('ordering', sortValue)
          setSearchParams(searchParams.toString())
        }
      },
      [activeSort, searchParams],
    )
    const dragStartHandler = (title: IHeaderData) => setCurrentDataTitle(title)
    const dragEndHandler = (e: React.DragEvent) =>
      ((e.target as HTMLDivElement).style.opacity = '1')
    const dragOverHandler = (e: React.DragEvent) => {
      e.preventDefault()
      ;(e.target as HTMLDivElement).style.opacity = '.3'
    }
    const dropHandler = (e: React.DragEvent, title: IHeaderData) => {
      e.preventDefault()
      const fromIndex = headerData.findIndex(
        (th) => th.title === currentDataTitle?.title,
      )
      const toIndex = headerData.findIndex((th) => th.title === title.title)

      if (
        fromIndex !== -1 &&
        toIndex !== -1 &&
        fromIndex !== 0 &&
        toIndex !== 0
      ) {
        setHeaderData(moveItem(headerData, fromIndex, toIndex))
        localStorage.setItem(
          tableName,
          JSON.stringify(moveItem(headerData, fromIndex, toIndex)),
        )
      }
      ;(e.target as HTMLDivElement).style.opacity = '1'
    }
    return (
      <thead>
        {mobileMode && headerData.length > 1 ? (
          <tr>
            {chooseMode && <th />}
            <th>
              <div className={'thead-th'} style={{ height: '100%' }}>
                <div className={'title'}>
                  {!chooseMode && (
                    <div
                      className={`table-toggle-block${
                        isAllOpen ? '' : ' active'
                      }`}
                      onClick={onToggleAll}
                    >
                      <i className="an-ico an-ico-arrow-down" />
                    </div>
                  )}
                  {t(headerData[0].title)}
                </div>
              </div>
            </th>
            {headerData
              .slice(currentCol, currentCol + (width < 600 ? 1 : 3))
              .map((e: any) => (
                <th key={e.title} className="second-col" {...touchFn}>
                  {t(e?.title)}
                </th>
              ))}
          </tr>
        ) : (
          <tr>
            {chooseMode && <th />}
            {headerData.map((th, index) => {
              return index === 0 && onToggleAll ? (
                <th
                  draggable={!!th.order || th.order === 0}
                  onDragStart={() => dragStartHandler(th)}
                  onDragLeave={dragEndHandler}
                  onDragEnd={dragEndHandler}
                  onDragOver={dragOverHandler}
                  onDrop={(e) => dropHandler(e, th)}
                  key={th.title + index}
                  className={`desktop-view ${
                    th.order === 0 ? 'desktop-view-name-stiky' : ''
                  }`}
                >
                  <div className={'thead-th'} style={{ height: '100%' }}>
                    <div className={'title'}>
                      <div
                        className={`table-toggle-block${
                          isAllOpen ? '' : ' active'
                        }`}
                        onClick={onToggleAll}
                      >
                        <i className="an-ico an-ico-arrow-down" />
                      </div>
                      {th.order !== 0 && (
                        <i className="an-ico an-ico-dots-grid" />
                      )}
                      {t(th.title)}
                    </div>

                    <div
                      onClick={
                        th.sort ? () => onSortHandler(th.sort!) : undefined
                      }
                      className={'thead-th'}
                      style={{ height: '100%' }}
                    >
                      {!!th.sort && (
                        <i
                          className={`an-ico an-ico-arrow-down-bg ico-sort ${
                            activeSort?.name === th.sort
                              ? activeSort?.activeField === `-${th.sort}`
                                ? 'active-sort asc'
                                : 'active-sort'
                              : ''
                          }`}
                        />
                      )}
                    </div>
                  </div>
                </th>
              ) : (
                <th
                  key={th.title + index}
                  className="desktop-view"
                  onClick={th.sort ? () => onSortHandler(th.sort!) : undefined}
                  draggable={true}
                  onDragStart={() => dragStartHandler(th)}
                  onDragLeave={dragEndHandler}
                  onDragEnd={dragEndHandler}
                  onDragOver={dragOverHandler}
                  onDrop={(e) => dropHandler(e, th)}
                >
                  <div className={'thead-th'}>
                    {th.order !== 0 && (
                      <i className="an-ico an-ico-dots-grid" />
                    )}
                    {t(th.title)}
                    {!!th.sort && (
                      <i
                        className={`an-ico an-ico-arrow-down-bg ico-sort ${
                          activeSort?.name === th.sort
                            ? activeSort?.activeField === `-${th.sort}`
                              ? 'active-sort asc'
                              : 'active-sort'
                            : ''
                        }`}
                      />
                    )}
                  </div>
                </th>
              )
            })}
          </tr>
        )}
      </thead>
    )
  },
)
